import * as Sentry from '@sentry/react'
import axios from 'axios'
import { lazy, useEffect, Suspense } from 'react'
import { Route, Router, Switch } from 'react-router-dom'

import { setErrorReporter } from '@scouts/bookkeeping'
import {
    getIsMobileAppMode,
    mixpanelInit,
    useInitFeatureFlags,
    useSetTitle,
    ChunkLoadErrorBoundary,
    Locale,
} from '@scouts/helpers'
import { TranslationProvider } from '@scouts/i18n'
import { SingularProvider } from '@scouts/singular'
import { colors, GlobalSpinner, Toaster, TooltipProvider } from '@scouts/ui'

import config from '@/config'
import { PATH_LOGOUT, PATH_MAGIC_LINK, PATH_MOBILE_NOTIFICATIONS, PATH_WELCOME } from '@/constants'
import { initSentry } from '@/helpers/sentry'
import history from '@/history'
import { handleSingularError, shouldInitializeSingularWeb, singularConfig } from '@/singular'

import { BookkeepingConfigurationProvider } from '@/bookkeeping-configuration'

import { useTaxfixAnnouncementModal } from './header/hooks/use-taxfix-announcement-modal'

import { AuthenticationBoundary } from './auth/AuthenticationBoundary'
import { AuthenticationProvider } from './auth/AuthenticationProvider'
import { Logout } from './auth/Logout'
import { MagicLinkResolver } from './auth/MagicLinkResolver'
import { ChunkLoadError } from './ChunkLoadError'
import { CookieConsent } from './CookieConsent'
import ErrorBoundary from './ErrorBoundary'
import { TaxfixAnnouncementModal } from './header/TaxfixAnnouncementModal'
import { InterceptorProvider } from './InterceptorProvider'
import { LocationObserver } from './LocationObserver'
import { MobileNotifications } from './mobile-notifications/MobileNotifications'
import { MobileVersionUpdateModal } from './MobileVersionUpdateModal'
import { PendingAgreement } from './pending-agreement'
import { PermissionProvider } from './permission-context'
import { RequestObserver } from './RequestObserver'
import WelcomeContainer from './welcome/WelcomeContainer'

// Move this to a separate file
axios.defaults.baseURL = config.apiUrl

const WelcomeContainerSuspense = () => (
    <Suspense fallback={<GlobalSpinner />}>
        <WelcomeContainer />
    </Suspense>
)

const Main = lazy(() => import('./Main'))

const App = () => {
    const { isLoading } = useInitFeatureFlags()
    const isMobileAppMode = getIsMobileAppMode()
    const {
        closeTaxfixAnnouncementModal,
        isTaxfixAnnouncementModalOpen,
        openTaxfixAnnouncementModal,
        hasSeenTaxfixAnnouncement,
    } = useTaxfixAnnouncementModal()

    useSetTitle('Taxfix (antes TaxScouts)')

    useEffect(() => {
        // These two init funcs can be moved to a separate file, same as Axios baseURL init
        initSentry()
        setErrorReporter((error, context) => {
            Sentry.captureException(error, {
                tags: {
                    is_bookkeeping: true,
                    origin_app: 'es-customer',
                },
                extra: context,
            })
        })

        if (config.isMixpanelEnabled) {
            mixpanelInit({ token: config.mixpanelToken, apiHost: config.mixpanelApiHost })
        }
    }, [])

    // Temporary
    useEffect(() => {
        if (!isTaxfixAnnouncementModalOpen && !hasSeenTaxfixAnnouncement && isMobileAppMode) {
            openTaxfixAnnouncementModal()
        }
    }, [isTaxfixAnnouncementModalOpen, hasSeenTaxfixAnnouncement, openTaxfixAnnouncementModal, isMobileAppMode])

    if (isLoading) {
        return <GlobalSpinner color={colors.surface.attention} />
    }

    return (
        <ErrorBoundary>
            <SingularProvider
                config={singularConfig}
                onError={handleSingularError}
                autoInit={shouldInitializeSingularWeb()}
            >
                <MobileVersionUpdateModal mobileAppTargetVersion={config.mobileAppTargetVersion} />
                <TranslationProvider locale={Locale.ES}>
                    <AuthenticationProvider>
                        <InterceptorProvider>
                            <Router history={history}>
                                <RequestObserver />
                                <LocationObserver>
                                    <ChunkLoadErrorBoundary renderError={() => <ChunkLoadError />}>
                                        {isTaxfixAnnouncementModalOpen && (
                                            <TaxfixAnnouncementModal onClose={closeTaxfixAnnouncementModal} />
                                        )}
                                        <CookieConsent />
                                        <Toaster />
                                        <TooltipProvider />
                                        <Switch>
                                            <Route path={PATH_LOGOUT} component={Logout} />
                                            <Route exact path={PATH_MAGIC_LINK} component={MagicLinkResolver} />
                                            <Route path={PATH_MOBILE_NOTIFICATIONS} component={MobileNotifications} />
                                            <Route path={PATH_WELCOME} component={WelcomeContainerSuspense} />

                                            <AuthenticationBoundary>
                                                <PermissionProvider>
                                                    <PendingAgreement>
                                                        <BookkeepingConfigurationProvider>
                                                            <Suspense fallback={<GlobalSpinner />}>
                                                                <Main />
                                                            </Suspense>
                                                        </BookkeepingConfigurationProvider>
                                                    </PendingAgreement>
                                                </PermissionProvider>
                                            </AuthenticationBoundary>
                                        </Switch>
                                    </ChunkLoadErrorBoundary>
                                </LocationObserver>
                            </Router>
                        </InterceptorProvider>
                    </AuthenticationProvider>
                </TranslationProvider>
            </SingularProvider>
        </ErrorBoundary>
    )
}

export default App
